import React, { useEffect, useMemo, useState } from "react";

import { faEllipsis, faMagnifyingGlass, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faGlobe } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	ButtonStandard,
	DropDownMenu,
	Icon,
	InputText,
	type PYColumnDef,
	Pagination,
	SingleSelectInput,
	Skeleton,
	Table,
	Text,
	Tooltip,
	toast,
} from "@performyard/py-components";
import type { CompetencyDict } from "@performyard/service-contracts";

import { BulkEditBar } from "../../../../Competencies/components/BulkEditBar";
import { BulkEditToggleButton } from "../../../../Competencies/components/BulkEditToggleButton";
import DiscardChangesModal from "../../../../Competencies/components/DiscardChangesModal";
import { CaretCell, RoleCompetencySuperRowContent, useSuperRow } from "../../../../Competencies/components/SuperRow";
import type { RoleCompetencyDetail, SuperRowContext, SuperRowRow } from "../../../../Competencies/components/SuperRow";
import useBulkLevelEdit, { useBulkLevelEditNavGuard } from "../../../../Competencies/components/useBulkLevelEdit";
import CompetencyPickerModal from "./CompetencyPickerModal";
import RemoveConfirmModal from "./RemoveConfirmModal";

import { useCompetencies } from "../../../../../api/hooks/defs/competencies";
import {
	useBulkPatchRoleCompetencies,
	usePatchRoleCompetency,
	useRoleCompetencies,
} from "../../../../../api/hooks/defs/roles";

import {
	NO_EXPECTED_LEVEL_LABEL,
	levelSelectOptions,
	rankToSelectValue,
	selectValueToRank,
} from "../../../../Competencies/utils";

export interface CompetenciesTabProps {
	roleId: string;
	readOnly?: boolean;
	// Lets the page-level archive flow warn about unsaved level edits before discarding them.
	onPendingCountChange?: (count: number) => void;
	// Lets the page disable archiving while the bulk save is in flight — the PATCH can't be
	// cancelled, so an archive mid-save would "discard" edits that still commit.
	onSavingChange?: (isSaving: boolean) => void;
}

const isLoadingRow = (data: Partial<RoleCompetencyDetail>) => !data.competency_id;

// 25 per page per design, matching the company-goals bulk-update table.
const PAGE_SIZE = 25;

const CompetenciesTab: React.FC<CompetenciesTabProps> = ({
	roleId,
	readOnly = false,
	onPendingCountChange,
	onSavingChange,
}) => {
	const [search, setSearch] = useState("");
	const [pageNumber, setPageNumber] = useState(1);
	const [pickerMode, setPickerMode] = useState<"add" | "edit" | null>(null);
	const [editTarget, setEditTarget] = useState<RoleCompetencyDetail | null>(null);
	const [removeTarget, setRemoveTarget] = useState<RoleCompetencyDetail | null>(null);

	const { data, isLoading } = useRoleCompetencies(roleId);
	// Same 500-competency cap as the create/edit form's RoleCompetencyList — see that file for context.
	const { data: allCompetencies } = useCompetencies({ status: "active", limit: 500 });
	const { mutate } = usePatchRoleCompetency(roleId, {
		onSuccess: () => {
			setPickerMode(null);
			setEditTarget(null);
			setRemoveTarget(null);
		},
		onError: (error) => {
			toast.error(
				error instanceof Error ? error.message : "An error occurred while updating the role's competencies."
			);
		},
	});

	const competencies = useMemo<RoleCompetencyDetail[]>(() => data?.competencies ?? [], [data]);

	// Sorted by name because the endpoint returns the role document's stored array order, and every
	// write reorders it — patchCompetency appends the entry it just edited, so an unsorted table
	// visibly jumps that row to the bottom. The columns aren't user-sortable, so this is the order.
	const filtered = useMemo(() => {
		const trimmed = search.trim().toLowerCase();
		const matching = trimmed ? competencies.filter((c) => c.name.toLowerCase().includes(trimmed)) : competencies;
		return [...matching].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
	}, [competencies, search]);

	const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	// Removing rows (or narrowing a search) can strand pageNumber past the end — show the nearest
	// real page. Pending edits live above the grid, so they survive page changes untouched.
	const currentPage = Math.min(pageNumber, pageCount);
	const paged = useMemo(
		() => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
		[filtered, currentPage]
	);

	// useSuperRow's height estimate reads levels off a CompetencyDict; this endpoint already joins
	// name/description/levels onto each row, so build the dict straight from what's on screen.
	const competenciesDict: CompetencyDict = useMemo(
		() =>
			Object.fromEntries(
				paged.map((c) => [
					c.competency_id,
					{ name: c.name, description: c.description, is_company_wide: c.is_company_wide, levels: c.levels },
				])
			),
		[paged]
	);

	const superRow = useSuperRow({
		rows: paged,
		competencies: competenciesDict,
		getRowId: (row) => row.competency_id,
		getInitialExpandedRank: (row) => row.expected_level,
	});

	const bulkPatchCompetencies = useBulkPatchRoleCompetencies(roleId);
	const bulkLevelEdit = useBulkLevelEdit({
		onSave: async (edits) => {
			const response = await bulkPatchCompetencies.mutateAsync({
				competencies: edits.map((edit) => ({ competency_id: edit.id, expected_level: edit.value })),
			});
			return { successfulIds: response.successful, failedIds: response.failed.map((f) => f.competency_id) };
		},
		successMessage: "Expected levels updated",
		failureMessage: (count) => `${count} expected level${count === 1 ? "" : "s"} failed to update`,
		errorMessage: "Failed to update expected levels",
		scopeKey: roleId,
	});
	// Tabs render through Radix without forceMount, so switching to Employees unmounts this tab and
	// takes the pending edits with it — the one search-param change here that must prompt.
	useBulkLevelEditNavGuard(bulkLevelEdit.pendingCount, { blockSearchParamChanges: true });

	// Report pending edits up to the page so its archive confirm can warn before discarding them;
	// unmounting (tab switch) drops the edits, so it reports zero.
	const { pendingCount: reportedPendingCount } = bulkLevelEdit;
	useEffect(() => {
		onPendingCountChange?.(reportedPendingCount);
		return () => onPendingCountChange?.(0);
	}, [reportedPendingCount, onPendingCountChange]);

	// Same for the in-flight save — an unmount mid-flight reports false so the page's archive
	// action doesn't stay locked by a tab that no longer exists.
	const { isSaving: reportedIsSaving } = bulkLevelEdit;
	useEffect(() => {
		onSavingChange?.(reportedIsSaving);
		return () => onSavingChange?.(false);
	}, [reportedIsSaving, onSavingChange]);

	// Archiving from the header flips readOnly mid-session, which hides Save and Cancel — end the
	// session rather than strand pending edits behind a guard the user can no longer clear.
	const { editMode, pendingCount, endEdit } = bulkLevelEdit;
	useEffect(() => {
		if (!readOnly || (!editMode && pendingCount === 0)) return;
		endEdit();
		if (pendingCount > 0) {
			toast.error(`Archiving discarded ${pendingCount} unsaved level change${pendingCount === 1 ? "" : "s"}.`);
		}
	}, [readOnly, editMode, pendingCount, endEdit]);
	// Defensive: the bulk-edit button is already hidden when readOnly, so editMode can't normally be
	// entered on an archived role — this just guards against that state combination directly too.
	const editingEnabled = bulkLevelEdit.editMode && !readOnly;

	const cwOptions = [{ key: "edit", label: "Edit Expected Level" }];
	const nonCwOptions = [
		{ key: "edit", label: "Edit Expected Level" },
		{ key: "remove", label: "Remove" },
	];

	const handleAction = (key: string, row: RoleCompetencyDetail) => {
		if (key === "edit") {
			setEditTarget(row);
			setPickerMode("edit");
		} else if (key === "remove") {
			setRemoveTarget(row);
		}
	};

	const handleAdd = () => {
		setEditTarget(null);
		setPickerMode("add");
	};

	// PatchRoleCompetencyBody types competency_id as ObjectId (post-Zod-transform); the wire format is a
	// string and the backend re-parses, so the unknown cast bridges the type-level gap.
	const handlePickerSubmit = (input: { competency_id: string; expected_level: number | null }) => {
		mutate({
			op: "upsert",
			competency_id: input.competency_id,
			expected_level: input.expected_level,
		} as unknown as Parameters<typeof mutate>[0]);
	};

	const handleRemoveConfirm = () => {
		if (!removeTarget) return;
		mutate({ op: "remove", competency_id: removeTarget.competency_id } as unknown as Parameters<typeof mutate>[0]);
	};

	const columns: PYColumnDef<SuperRowRow<RoleCompetencyDetail>>[] = [
		{
			colId: "super_row_caret",
			headerName: "",
			sortable: false,
			width: 40,
			resizable: false,
			suppressSizeToFit: true,
			// !p-0 kills the theme's cell padding — the caret's 16px left offset lives in CaretCell.
			cellClass: "!p-0",
			cellStyle: { display: "flex", alignItems: "center" },
			cellRenderer: CaretCell,
		},
		{
			colId: "name",
			headerName: "Competency",
			flex: 2,
			cellRenderer: (params: { data: SuperRowRow<RoleCompetencyDetail>; context?: SuperRowContext }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				const isOpen = params.context?.expandedRowId === row._rowId;
				return (
					<div className="flex min-w-0 items-center gap-2">
						<Text
							size="paragraph-3"
							weight="regular"
							color={isOpen ? "primary-5" : undefined}
							dangerouslySetClassName="min-w-0"
						>
							<span className="block py-text-truncate">{row.name}</span>
						</Text>
						{row.is_company_wide && (
							<Tooltip content="Company Wide Competency">
								<span
									data-testid={`cw-icon-${row.competency_id}`}
									className="flex size-4 shrink-0 items-center justify-center"
								>
									<FontAwesomeIcon icon={faGlobe} className="text-sm text-primary-5" />
								</span>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			colId: "expected_level",
			headerName: "Expected Level",
			// Hugs the 160px edit select (PY-14091).
			width: 184,
			valueGetter: (params: { data?: RoleCompetencyDetail }) => params.data?.expected_level ?? null,
			cellRenderer: (params: { data: RoleCompetencyDetail }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-12" />;
				if (editingEnabled && row.levels.length > 0) {
					const pendingValue =
						row.competency_id in bulkLevelEdit.pendingEdits
							? bulkLevelEdit.pendingEdits[row.competency_id]
							: row.expected_level;
					const levelOptions = levelSelectOptions(row.levels, NO_EXPECTED_LEVEL_LABEL);
					const selectedLabel = levelOptions.find(
						(option) => option.value === rankToSelectValue(pendingValue)
					)?.label;
					return (
						<div className="w-[160px]" title={selectedLabel}>
							<SingleSelectInput<number>
								value={rankToSelectValue(pendingValue)}
								options={levelOptions}
								onChange={(value) =>
									bulkLevelEdit.setPendingEdit(
										row.competency_id,
										selectValueToRank(value),
										row.expected_level
									)
								}
								disabled={
									bulkLevelEdit.isSaving ||
									(bulkLevelEdit.atCap && !(row.competency_id in bulkLevelEdit.pendingEdits))
								}
								ariaLabel={`Expected level for ${row.name}`}
							/>
						</div>
					);
				}
				if (row.expected_level === null) {
					return (
						<Text size="paragraph-3" weight="regular" color="neutral-7">
							—
						</Text>
					);
				}
				const lvl = row.levels.find((l) => l.rank === row.expected_level);
				const label = lvl ? lvl.name : `Level ${row.expected_level}`;
				return (
					<Text size="paragraph-3" weight="regular">
						{label}
					</Text>
				);
			},
		},
		{
			colId: "actions",
			headerName: "",
			headerClass: "!pr-1",
			cellClass: "!pr-1",
			sortable: false,
			width: 50,
			resizable: false,
			suppressSizeToFit: true,
			hide: readOnly,
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "8px" },
			cellRenderer: (params: { data: RoleCompetencyDetail }) => {
				const row = params.data;
				if (isLoadingRow(row)) return null;
				// The column stays mounted during a bulk-edit session so the layout doesn't shift —
				// only the kebab goes: a remove mid-session strands its pending edit, which the
				// upsert-only save resurrects.
				if (editingEnabled) return null;
				return (
					<DropDownMenu
						options={row.is_company_wide ? cwOptions : nonCwOptions}
						onSelect={(key) => handleAction(key, row)}
						placement="bottom-end"
						minWidth={80}
					>
						<Icon icon={faEllipsis} size={24} color="neutral-5" />
					</DropDownMenu>
				);
			},
		},
	];

	return (
		<div className="py-4" data-testid="role-detail-competencies-tab">
			<div className="mb-4 flex items-center justify-between gap-4">
				<div className="w-72">
					<InputText
						id="competency-search"
						value={search}
						onChange={(v) => {
							setSearch(v);
							setPageNumber(1);
						}}
						placeholder="Search competencies..."
						leftIcon={faMagnifyingGlass}
					/>
				</div>
				{!readOnly && (
					<div className="flex items-center gap-2">
						{
							// Nothing to bulk-edit on a role with no competencies — gated on the unfiltered list
							// so a search with no matches keeps the button. Once a session is open, keep it
							// mounted regardless (e.g. the last row getting removed mid-edit) so it never
							// vanishes out from under an active edit.
							(competencies.length > 0 || bulkLevelEdit.editMode) && (
								<BulkEditToggleButton
									editMode={bulkLevelEdit.editMode}
									manageLabel="Manage Expected Levels"
									editingLabel="Editing Expected Levels"
									disabled={bulkLevelEdit.isSaving}
									onEnter={bulkLevelEdit.enterEditMode}
									onRequestExit={bulkLevelEdit.requestExit}
								/>
							)
						}
						<ButtonStandard
							color="primary"
							iconLeft={faPlus}
							onClick={handleAdd}
							disabled={bulkLevelEdit.editMode}
							data-testid="add-competency-button"
						>
							Add Competency
						</ButtonStandard>
					</div>
				)}
			</div>

			<BulkEditBar
				isOpen={editingEnabled}
				pendingCount={bulkLevelEdit.pendingCount}
				atCap={bulkLevelEdit.atCap}
				isSaving={bulkLevelEdit.isSaving}
				onCancel={bulkLevelEdit.requestExit}
				onSave={() => void bulkLevelEdit.save()}
			/>
			{/* Square the table's top corners while the edit bar sits flush on top (PY-14103). The !
			    is load-bearing: ag's runtime-injected theme CSS is unlayered and beats Tailwind v4's
			    layered utilities at any specificity. */}
			<div className={editingEnabled ? "[&_.ag-root-wrapper]:!rounded-t-none" : undefined}>
				<Table<SuperRowRow<RoleCompetencyDetail>>
					ref={superRow.gridRef}
					autoHeight
					columnDefs={columns}
					rowData={superRow.rowData}
					loading={isLoading}
					getRowId={(params) => params.data._rowId}
					rowHeight={48}
					context={superRow.contextRef.current}
					isFullWidthRow={superRow.isFullWidthRow}
					fullWidthCellRenderer={RoleCompetencySuperRowContent}
					getRowHeight={superRow.getRowHeight}
					defaultColDef={{
						cellStyle: { display: "flex", alignItems: "center" },
						resizable: false,
						suppressMovable: true,
					}}
					emptyStateText="No Competencies Found"
				/>
			</div>

			{pageCount > 1 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination currentPage={currentPage} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}

			{pickerMode && (
				<CompetencyPickerModal
					isOpen
					mode={pickerMode}
					roleCompetencies={competencies}
					allActiveCompetencies={allCompetencies?.competencies ?? []}
					editTarget={editTarget ?? undefined}
					onSubmit={handlePickerSubmit}
					onClose={() => {
						setPickerMode(null);
						setEditTarget(null);
					}}
				/>
			)}

			{removeTarget && (
				<RemoveConfirmModal
					isOpen
					competencyName={removeTarget.name}
					onConfirm={handleRemoveConfirm}
					onClose={() => setRemoveTarget(null)}
				/>
			)}

			<DiscardChangesModal
				isOpen={bulkLevelEdit.confirmingExit}
				pendingCount={bulkLevelEdit.pendingCount}
				onConfirm={bulkLevelEdit.confirmExit}
				onClose={bulkLevelEdit.dismissExit}
			/>
		</div>
	);
};

export default CompetenciesTab;
